import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import AppShell from "@/components/AppShell";
import {
  ShieldAlert,
  Database,
  Settings as SettingsIcon,
  Users as UsersIcon,
  Wallet,
  Activity,
  Bot,
  Building2,
  BrainCircuit,
  Send,
} from "lucide-react";

import UsersTab        from "./admin/UsersTab";
import PlansTab        from "./admin/PlansTab";
import ConfigTab       from "./admin/ConfigTab";
import PaymentsTab     from "./admin/PaymentsTab";
import MarketBudgetTab from "./admin/MarketBudgetTab";
import Mt5Tab          from "./admin/Mt5Tab";
import BrokersTab      from "./admin/BrokersTab";
import MlTab           from "./admin/MlTab";
import TelegramTab     from "./admin/TelegramTab";
import LiveActivityFeed from "./admin/LiveActivityFeed";

const TABS = [
  { id: "users",    label: "Users",            icon: UsersIcon,     Component: UsersTab },
  { id: "mt5",      label: "MT5 Accounts",     icon: Bot,           Component: Mt5Tab },
  { id: "brokers",  label: "Brokers",          icon: Building2,     Component: BrokersTab },
  { id: "ml",       label: "ML Models",        icon: BrainCircuit,  Component: MlTab },
  { id: "telegram", label: "Telegram",         icon: Send,          Component: TelegramTab },
  { id: "plans",    label: "Plans",            icon: Database,      Component: PlansTab },
  { id: "config",   label: "Wallets & Config", icon: SettingsIcon,  Component: ConfigTab },
  { id: "payments", label: "Payments",         icon: Wallet,        Component: PaymentsTab },
  { id: "market",   label: "Market budget",    icon: Activity,      Component: MarketBudgetTab },
];

/** Super-Admin dashboard — gated by user.role === "admin". */
export default function AdminPage() {
  const { user, subscription } = useAuth();
  const [tab, setTab] = useState("users");
  // Guard renders AFTER hooks to satisfy rules-of-hooks
  if (user === undefined) return null;
  if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const Active = TABS.find((t) => t.id === tab)?.Component || UsersTab;

  return (
    <AppShell>
      <div className="px-6 py-8 space-y-6 max-w-7xl" data-testid="admin-page">
        <header className="space-y-2">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-amber-300">
            <ShieldAlert className="size-4" /> Super-Admin
          </div>
          <h1 className="heading text-3xl md:text-4xl font-bold tracking-tighter">
            Control <span className="text-gold-grad">Tower</span>
          </h1>
          <p className="text-sm text-white/60">
            Logged in as <span className="text-white">{user.email}</span> · Subscription:{" "}
            <span className="text-amber-200">{subscription?.plan || "—"}</span>
          </p>
        </header>

        <nav className="flex flex-wrap gap-2" data-testid="admin-tabs">
          {TABS.map((t) => {
            const I = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium inline-flex items-center gap-1.5 ${
                  tab === t.id ? "bg-amber-400/90 text-ink-900" : "bg-white/5 text-white/70 hover:bg-white/10"
                }`}
              >
                <I className="size-3.5" />
                {t.label}
              </button>
            );
          })}
        </nav>

        <LiveActivityFeed />

        <Active />
      </div>
    </AppShell>
  );
}
